import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Repeat, Video, MapPin, Plus, Minus, History, Ticket, ArrowUpRight, Paperclip, CalendarDays } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { getEvents, getPublicEvents, getPublicEventsPast, API } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { resolveEventTimes, weekdayLongName } from "@/lib/dateUtils";
import { MAPS_URL } from "@/lib/constants";

const Flag = ({ code, label }) => (
  <img
    src={`https://flagcdn.com/w40/${code}.png`}
    srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
    alt={label}
    width={22}
    height={16}
    className="rounded-[2px] shadow-sm ring-1 ring-black/10 shrink-0"
  />
);

const PastFixedRow = ({ ev, lang }) => {
  const { tr } = useLanguage();
  const c = tr(ev.translations);
  const { weekdayLabel, dateLabel } = resolveEventTimes(ev, lang);
  return (
    <li className="py-5 border-b border-border/60 last:border-0" data-testid={`past-event-${ev.id}`}>
      <p className="font-sans text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
        {[weekdayLabel, dateLabel].filter(Boolean).join(", ")}
      </p>
      <h4 className="mt-1.5 font-serif text-xl text-foreground">{c.title}</h4>
      <p className="mt-1 font-sans text-sm font-light text-muted-foreground">{c.description}</p>
    </li>
  );
};

// Linha do acervo: evento avulso publicado que já aconteceu — tipo, data, título,
// anfitrião e miniatura do flyer (clique abre a imagem). Sem horário.
const PastCustomRow = ({ ev, lang, t }) => {
  const d = new Date(ev.date + "T12:00:00");
  const dateLabel = new Intl.DateTimeFormat(lang, {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  }).format(d);
  const kindLabel = ev.kind === "palestra" ? t("activities.cats.lecture")
    : ev.kind === "estudo" ? t("activities.cats.study") : t("activities.specialEvent");
  const isPdf = (ev.flyer_name || "").toLowerCase().endsWith(".pdf");
  return (
    <li className="py-5 border-b border-border/60 last:border-0 flex gap-4 items-start" data-testid={`past-event-${ev.session_id}`}>
      {ev.flyer_path && !isPdf && (
        <a href={`${API}/files/${ev.flyer_path}`} target="_blank" rel="noopener noreferrer" className="shrink-0" data-testid={`past-flyer-${ev.session_id}`}>
          <img src={`${API}/files/${ev.flyer_path}`} alt={ev.title} className="h-20 w-auto rounded-md border border-border object-cover hover:opacity-90 transition-opacity" loading="lazy" />
        </a>
      )}
      <div className="min-w-0">
        <p className="font-sans text-[11px] uppercase tracking-[0.15em] text-muted-foreground capitalize">
          {kindLabel} · {dateLabel}
        </p>
        <h4 className="mt-1.5 font-serif text-xl text-foreground">{ev.title}</h4>
        {ev.host && (
          <p className="mt-1 font-sans text-sm font-light text-muted-foreground">{t("activities.specialWith")} {ev.host}</p>
        )}
        {ev.flyer_path && isPdf && (
          <a href={`${API}/files/${ev.flyer_path}`} target="_blank" rel="noopener noreferrer" data-testid={`past-flyer-${ev.session_id}`}
            className="mt-1.5 inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.15em] text-primary hover:text-primary/70 transition-colors">
            <Paperclip size={12} strokeWidth={2} />
            {t("activities.viewAttachment")}
          </a>
        )}
      </div>
    </li>
  );
};

export const Activities = () => {
  const { t, tr, lang } = useLanguage();
  const { data: events = [], isLoading } = useQuery({ queryKey: ["events"], queryFn: getEvents });
  const { data: specials = [] } = useQuery({ queryKey: ["public-events"], queryFn: getPublicEvents });
  const { data: pastCustoms = [] } = useQuery({ queryKey: ["public-events-past"], queryFn: getPublicEventsPast });
  const [expanded, setExpanded] = useState({});
  const [pastOpen, setPastOpen] = useState(false);

  const activeEvents = events.filter((e) => !e.is_past);
  const pastEvents = events.filter((e) => e.is_past);

  // Lista única em ordem cronológica: encontros fixos + eventos avulsos publicados
  // (sem seção separada — o visitante vê tudo como uma agenda só, na sequência das datas)
  const rows = React.useMemo(() => {
    const fixed = activeEvents.map((ev) => ({ type: "fixed", ev, sortKey: resolveEventTimes(ev, lang).sortKey || "9999" }));
    const customs = specials.map((ev) => ({ type: "custom", ev, sortKey: `${ev.date}T${ev.time || "00:00"}` }));
    return [...fixed, ...customs].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, specials, lang]);

  const weekdayName = (weekday) => {
    const arr = t("activities.weekdays") || [];
    return arr[weekday] || "";
  };

  return (
    <section id="activities" className="py-24 md:py-36 bg-secondary/40 border-y border-border" data-testid="activities">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-8">
            <Reveal>
              <span className="font-sans uppercase tracking-[0.25em] text-xs font-semibold text-primary">
                {t("activities.overline")}
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-serif text-4xl sm:text-6xl tracking-tight text-foreground">
                {t("activities.title")}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-4">
            <p className="font-sans text-base leading-relaxed font-light text-muted-foreground">
              {t("activities.intro")}
            </p>
          </Reveal>
        </div>

        <div className="border-t border-border" data-testid="events-list">
          {isLoading &&
            [...Array(2)].map((_, i) => (
              <div key={i} className="h-32 border-b border-border animate-pulse bg-foreground/[0.03]" />
            ))}

          {rows.map((row, i) => {
            if (row.type === "custom") {
              const ev = row.ev;
              const d = new Date(ev.date + "T12:00:00");
              const weekdayLbl = new Intl.DateTimeFormat(lang, { weekday: "long" }).format(d);
              const dateLbl = new Intl.DateTimeFormat(lang, { day: "2-digit", month: "long", year: "numeric" }).format(d);
              const kindLabel = ev.kind === "palestra" ? t("activities.cats.lecture")
                : ev.kind === "estudo" ? t("activities.cats.study") : t("activities.specialEvent");
              const isPdf = (ev.flyer_name || "").toLowerCase().endsWith(".pdf");
              return (
                <Reveal
                  as="div"
                  key={ev.session_id}
                  delay={Math.min(i * 0.06, 0.3)}
                  data-testid={`event-row-${ev.session_id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start py-10 border-b border-border"
                >
                  <div className="md:col-span-3">
                    <span className="font-serif text-xl leading-snug text-primary block capitalize">{weekdayLbl}</span>
                    <span className="mt-1 block font-sans text-sm text-muted-foreground">{dateLbl}</span>
                    <div className="mt-3 flex flex-col gap-1.5">
                      <span className="flex items-center gap-2.5 font-sans text-sm text-foreground">
                        <Flag code="ch" label="Schweiz" />
                        <span className="tabular-nums">{ev.time}h</span>
                      </span>
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <h3 className="font-serif text-2xl text-foreground">{ev.title}</h3>
                    {ev.flyer_path && (
                      <div className="mt-3">
                        {!isPdf && (
                          <a href={`${API}/files/${ev.flyer_path}`} target="_blank" rel="noopener noreferrer" className="inline-block" data-testid={`event-flyer-${ev.session_id}`}>
                            <img src={`${API}/files/${ev.flyer_path}`} alt={ev.title} className="max-h-36 w-auto rounded-lg border border-border object-cover hover:opacity-90 transition-opacity" loading="lazy" />
                          </a>
                        )}
                        <a
                          href={`${API}/files/${ev.flyer_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-attachment-${ev.session_id}`}
                          className="mt-2 inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.15em] text-primary hover:text-primary/70 transition-colors"
                        >
                          <Paperclip size={12} strokeWidth={2} />
                          {t("activities.viewAttachment")}
                        </a>
                      </div>
                    )}
                    <span className="mt-6 flex w-fit font-sans text-[11px] uppercase tracking-[0.15em] text-muted-foreground items-center gap-1.5">
                      <CalendarDays size={11} strokeWidth={1.5} />
                      {kindLabel}{ev.host ? ` · ${t("activities.specialWith")} ${ev.host}` : ""}
                    </span>
                  </div>
                  <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                    <div className="flex flex-wrap gap-3 md:justify-end">
                      {(ev.fmt || "onsite") !== "onsite" && (
                        <a
                          href={ev.online_link || "#contact"}
                          target={ev.online_link ? "_blank" : undefined}
                          rel={ev.online_link ? "noopener noreferrer" : undefined}
                          data-testid={`event-online-${ev.session_id}`}
                          className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-sans text-sm tracking-wide hover:bg-primary/90 transition-colors duration-300"
                        >
                          <Video size={16} strokeWidth={1.5} />
                          {t("activities.formats.online")}
                        </a>
                      )}
                      {(ev.fmt || "onsite") !== "online" && (
                        <a
                          href={MAPS_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-onsite-${ev.session_id}`}
                          className="group inline-flex items-center gap-2 rounded-full border border-primary/40 text-foreground px-6 py-3 font-sans text-sm tracking-wide hover:bg-primary/10 hover:border-primary transition-colors duration-300"
                        >
                          <MapPin size={16} strokeWidth={1.5} className="text-primary" />
                          {t("activities.formats.onsite")}
                        </a>
                      )}
                    </div>
                    {(ev.fmt || "onsite") !== "onsite" && (
                      <p className="font-sans text-xs text-muted-foreground md:text-right max-w-[280px] md:ml-auto leading-relaxed" data-testid={`event-online-note-${ev.session_id}`}>
                        {t("activities.zoom.contactNote")}
                      </p>
                    )}
                  </div>
                </Reveal>
              );
            }
            const ev = row.ev;
            const c = tr(ev.translations);
            const { weekdayLabel, dateLabel, timeCh, timeBr, isOnce } = resolveEventTimes(ev, lang);
            const isOpen = !!expanded[ev.id];
            return (
              <Reveal
                as="div"
                key={ev.id}
                delay={Math.min(i * 0.06, 0.3)}
                data-testid={`event-row-${ev.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start py-10 border-b border-border"
              >
                {/* Weekday (title style) + date (subtitle) + times with flags */}
                <div className="md:col-span-3">
                  <span className="font-serif text-xl leading-snug text-primary block">
                    {weekdayLabel}
                  </span>
                  <span className="mt-1 block font-sans text-sm text-muted-foreground">
                    {dateLabel}
                  </span>
                  <div className="mt-3 flex flex-col gap-1.5">
                    <span className="flex items-center gap-2.5 font-sans text-sm text-foreground">
                      <Flag code="ch" label="Schweiz" />
                      <span className="tabular-nums">{timeCh}</span>
                    </span>
                    {timeBr && ev.fmt !== "onsite" && (
                      <span className="flex items-center gap-2.5 font-sans text-sm text-foreground">
                        <Flag code="br" label="Brasil" />
                        <span className="tabular-nums">{timeBr}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Title + description + expandable body */}
                <div className="md:col-span-5">
                  <h3 className="font-serif text-2xl text-foreground">{c.title}</h3>
                  <p className="font-sans text-sm font-light text-muted-foreground mt-1.5 max-w-xl line-clamp-3">
                    {c.description}
                  </p>
                  {c.body && (
                    <>
                      <button
                        type="button"
                        onClick={() => setExpanded((p) => ({ ...p, [ev.id]: !p[ev.id] }))}
                        data-testid={`event-readmore-${ev.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.15em] text-primary hover:text-primary/70 transition-colors"
                      >
                        {isOpen ? <Minus size={12} strokeWidth={2} /> : <Plus size={12} strokeWidth={2} />}
                        {isOpen ? t("activities.readless") : t("activities.readmore")}
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="body"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 font-sans text-sm font-light text-muted-foreground max-w-xl whitespace-pre-line leading-relaxed">
                              {c.body}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                  <span className="mt-6 flex w-fit font-sans text-[11px] uppercase tracking-[0.15em] text-muted-foreground items-center gap-1.5">
                    <Repeat size={11} strokeWidth={1.5} />
                    {t(`activities.recur.${ev.recurrence}`)}
                    {ev.recurrence === "weekly" && <>· {t("activities.every")} {weekdayLongName(ev.weekday, lang)}</>}
                  </span>
                </div>

                {/* Action buttons */}
                <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                  <div className="flex flex-wrap gap-3 md:justify-end">
                    {ev.zoom?.link && (
                      <a
                        href={ev.zoom.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`event-online-${ev.id}`}
                        className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-sans text-sm tracking-wide hover:bg-primary/90 transition-colors duration-300"
                      >
                        <Video size={16} strokeWidth={1.5} />
                        {t("activities.formats.online")}
                      </a>
                    )}
                    {ev.locations ? (
                      ev.locations.map((loc) => (
                        <a
                          key={loc.url}
                          href={loc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-location-${ev.id}-${loc.city}`}
                          className="group inline-flex items-center gap-2 rounded-full border border-primary/40 text-foreground px-6 py-3 font-sans text-sm tracking-wide hover:bg-primary/10 hover:border-primary transition-colors duration-300"
                        >
                          <MapPin size={16} strokeWidth={1.5} className="text-primary" />
                          {t("activities.onsiteIn")} {t(`activities.cities.${loc.city}`)}
                        </a>
                      ))
                    ) : (
                      <a
                        href={MAPS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`event-onsite-${ev.id}`}
                        className="group inline-flex items-center gap-2 rounded-full border border-primary/40 text-foreground px-6 py-3 font-sans text-sm tracking-wide hover:bg-primary/10 hover:border-primary transition-colors duration-300"
                      >
                        <MapPin size={16} strokeWidth={1.5} className="text-primary" />
                        {t("activities.formats.onsite")}
                      </a>
                    )}
                    {ev.ticket_url && (
                      <a
                        href={ev.ticket_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`event-tickets-${ev.id}`}
                        className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-sans text-sm tracking-wide hover:bg-primary/90 transition-colors duration-300"
                      >
                        <Ticket size={16} strokeWidth={1.5} />
                        {t("activities.tickets")}
                      </a>
                    )}
                    {ev.info_url && (
                      <a
                        href={ev.info_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`event-info-${ev.id}`}
                        className="group inline-flex items-center gap-1.5 px-2 py-3 font-sans text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                      >
                        {t("activities.eventPage")}
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                      </a>
                    )}
                  </div>
                  {ev.zoom && (
                    <p className="font-sans text-xs text-muted-foreground md:text-right max-w-[280px] md:ml-auto leading-relaxed">
                      {t("activities.zoom.contactNote")}
                    </p>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        {(pastEvents.length > 0 || pastCustoms.length > 0) && (
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => setPastOpen(true)}
              data-testid="past-events-toggle"
              className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-primary transition-colors"
            >
              <History size={13} strokeWidth={1.5} />
              {t("activities.past")} ({pastEvents.length + pastCustoms.length})
            </button>
          </div>
        )}

        <Dialog open={pastOpen} onOpenChange={setPastOpen}>
          <DialogContent
            data-testid="past-events-modal"
            className="max-w-2xl w-[92vw] max-h-[80vh] overflow-y-auto bg-background text-foreground"
          >
            <DialogTitle className="font-serif text-2xl md:text-3xl text-foreground">
              {t("activities.past")}
            </DialogTitle>
            <ul className="mt-4">
              {[...pastCustoms.map((ev) => ({ type: "custom", ev, sortKey: ev.date })),
                ...pastEvents.map((ev) => ({ type: "fixed", ev, sortKey: ev.date || "0000" }))]
                .sort((a, b) => b.sortKey.localeCompare(a.sortKey))
                .map((row) => row.type === "custom" ? (
                  <PastCustomRow key={row.ev.session_id} ev={row.ev} lang={lang} t={t} />
                ) : (
                  <PastFixedRow key={row.ev.id} ev={row.ev} lang={lang} />
                ))}
            </ul>
          </DialogContent>
        </Dialog>

        <Reveal delay={0.1}>
          <p className="mt-10 font-sans text-sm font-light text-muted-foreground italic">{t("activities.note")}</p>
        </Reveal>
      </div>
    </section>
  );
};
